/* eslint-disable react/no-unused-prop-types */
import { useFormikContext } from 'formik';
import { useEffect, useState } from 'react';
import _ from 'lodash';
import { useSearchParams } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { store, useStore } from '../../../stores/store';
import {
  CountryDetails,
  ParcelDimensions,
} from '../../../models/shipmentCountryInfo';
import { FormValues } from './widget-calculator-form.component';
import { isFeatureRequired } from '../../../utils/client/client.utils';
import { ApiResponseStatus } from '../../../constants/server';

interface Props {
  showInput: (inputName: string, show: boolean) => void;
  // eslint-disable-next-line react/no-unused-prop-types
  isZipSearch?: boolean;
  setDestinationCountryFieldDisabled?: (disabled: boolean) => void;
}

export const DimensionFieldEffects = () => {
  const { values } = useFormikContext<FormValues>();
  const {
    widgetCalculatorStore: { setParcelDimensions },
  } = useStore();

  useEffect(() => {
    // guard for 'clear all' so user can create parcel with prev values
    if (!values.size_x && !values.size_y && !values.size_z && !values.weight)
      return;

    const newDimensions: ParcelDimensions = {
      size_x: values.size_x ?? 0,
      size_y: values.size_y ?? 0,
      size_z: values.size_z ?? 0,
      weight: values.weight ?? 0,
    };

    setParcelDimensions(newDimensions);
  }, [
    setParcelDimensions,
    values.size_x,
    values.size_y,
    values.size_z,
    values.weight,
  ]);

  return null;
};

export const DepartureFieldEffectsCommon = observer(() => {
  const {
    widgetCalculatorStore: { findShipmentCountry },
    shipmentCountriesStore: { senderCountries },
    localizationsStore: { selectedCountry },
    userStore: { user, isLoadingUser },
  } = useStore();

  const { setFieldValue } = useFormikContext<FormValues>();

  useEffect(() => {
    if (isLoadingUser) return;

    const selectedCountryCode = selectedCountry?.country_code;

    if (senderCountries.length <= 0 || !selectedCountryCode) return;

    const departureCountry = _.cloneDeep(
      findShipmentCountry(selectedCountryCode, undefined)
    );

    if (!departureCountry) return;

    // For 'Country' departure field we need to fill country in any case.
    setFieldValue('departure_country', departureCountry);

    const skipCapitalPrefill = isFeatureRequired(
      'calculatorSkipCapitalPrefillUnloggedUser'
    );

    // Get user's zip
    if (user) {
      if (user?.country === departureCountry?.code) {
        departureCountry.zipCode = user.address?.post_code;
        departureCountry.city = user.address?.city;
        setFieldValue('departure_country', departureCountry);
        return;
      }
      if (skipCapitalPrefill) return;
    }

    // Get capital if allowed
    if (!skipCapitalPrefill) {
      const capital = store.shipmentCountriesStore.getCapital(
        departureCountry.code
      );
      departureCountry.zipCode = capital?.default_zip;
      departureCountry.city = capital?.default_city;

      setFieldValue('departure_country', departureCountry);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [senderCountries, selectedCountry, user?.country, isLoadingUser]);

  return null;
});

export const DepartureFieldEffectsCountry = () => {
  const { setFieldValue, values } = useFormikContext<FormValues>();

  useEffect(() => {
    if (!values.departure_country) return;

    if (!values.departure_country.zipCode) {
      const capital = store.shipmentCountriesStore.getCapital(
        values.departure_country.code
      );
      values.departure_country.zipCode = capital?.default_zip;
      setFieldValue('departure_country', values.departure_country);
    }

    setFieldValue(
      'departure_search_string',
      values.departure_country.localized_name
    );
  }, [values.departure_country, setFieldValue]);

  return null;
};

export const DestinationFieldEffectsCountry = () => {
  const { values, setFieldValue, setFieldError } =
    useFormikContext<FormValues>();
  const {
    widgetCalculatorStore: { getDeliveryLimitations },
  } = useStore();

  useEffect(() => {
    async function reactToDestinationChange() {
      if (
        !values.departure_country ||
        !values.departure_search_string ||
        !values.destination_search_string
      )
        return;

      const foundDestinationCountry: CountryDetails | undefined = _.cloneDeep(
        store.shipmentCountriesStore.receiverCountries.find(
          (x) => x.localized_name === values.destination_search_string
        )
      );

      if (!foundDestinationCountry) {
        setFieldValue('destination_country', null);
        return;
      }

      const capital = store.shipmentCountriesStore.getCapital(
        foundDestinationCountry.code
      );

      foundDestinationCountry.zipCode = capital?.default_zip;
      foundDestinationCountry.city = capital?.default_city;

      setFieldValue('destination_country', foundDestinationCountry);

      let error = await getDeliveryLimitations(
        values.departure_country,
        foundDestinationCountry,
        true
      );

      if (!error) return;

      // retry with capital zip
      if (error === ApiResponseStatus.VALIDATION_ERROR.toString()) {
        const departureCapital = store.shipmentCountriesStore.getCapital(
          values.departure_country.code
        );
        const departureWithDefaultZip: CountryDetails = {
          ...values.departure_country,
          zipCode: departureCapital?.default_zip,
        };

        error = await getDeliveryLimitations(
          departureWithDefaultZip,
          foundDestinationCountry,
          false
        );

        if (!error) {
          // Update existing 'values.departure_country' to avoid effect retriggering
          values.departure_country.zipCode = departureWithDefaultZip?.zipCode;
          setFieldValue('departure_country', values.departure_country);
        }
      }

      if (error) {
        setFieldError('departure_country', error);
        setFieldError('destination_country', error);
      }
    }

    reactToDestinationChange();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.departure_country, values.destination_search_string]);

  return null;
};

interface Props {
  showInput: (inputName: string, show: boolean) => void;
  isZipSearch?: boolean;
  setDestinationCountryFieldDisabled?: (disabled: boolean) => void;
}

export const DepartureFieldEffectsZip = ({ showInput }: Props) => {
  const { values, setFieldValue } = useFormikContext<FormValues>();

  const [isDeparturePreselected, setIsDeparturePreselected] = useState(false);

  useEffect(() => {
    // Only run this useEffect on the initial run when departure country is pre-selected
    // based on locale's capital or user's address in DepartureFieldEffectsCommon
    if (!values.departure_country || isDeparturePreselected) return;

    const { zipCode, city } = values.departure_country;

    const departureSearchString = [zipCode, city].filter((x) => !!x).join(', ');
    if (!departureSearchString) return;

    setFieldValue('departure_search_string', departureSearchString);
    showInput('departure_search_string', true);

    setIsDeparturePreselected(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.departure_country, values.departure_search_string, setFieldValue]);

  return null;
};

export const DestinationFieldEffectsZip = () => {
  const { values, setFieldValue, setFieldError, isValid } =
    useFormikContext<FormValues>();
  const {
    widgetCalculatorStore: { getDeliveryLimitations },
  } = useStore();

  useEffect(() => {
    async function reactToDestinationChange() {
      if (
        !isValid ||
        !values.departure_country ||
        !values.destination_country ||
        !values.departure_country.zipCode ||
        !values.destination_country.zipCode
      )
        return;

      let error = await getDeliveryLimitations(
        values.departure_country,
        values.destination_country,
        true
      );

      if (error) {
        // retry with capital zip
        if (error === ApiResponseStatus.VALIDATION_ERROR.toString()) {
          const departureCapital = store.shipmentCountriesStore.getCapital(
            values.departure_country.code
          );
          const departureWithDefaultZip: CountryDetails = {
            ...values.departure_country,
            zipCode: departureCapital?.default_zip,
          };

          error = await getDeliveryLimitations(
            departureWithDefaultZip,
            values.destination_country,
            false
          );

          if (!error) {
            values.departure_country.zipCode = departureWithDefaultZip?.zipCode;
            setFieldValue('departure_country', values.departure_country);
            setFieldValue(
              'departure_search_string',
              departureWithDefaultZip.zipCode
            );
          }
        }

        if (error) {
          // In case 'error' is undefined then previous error on this field will be removed.
          setFieldError('departure_country', error);
          setFieldError('destination_country', error);
        }
      }
    }

    reactToDestinationChange();
  }, [
    values.departure_country,
    values.destination_country,
    isValid,
    getDeliveryLimitations,
    setFieldError,
    setFieldValue,
  ]);

  return null;
};

export const DestinationFieldEffectSearchParam = ({
  showInput,
  isZipSearch,
  setDestinationCountryFieldDisabled,
}: Props) => {
  const { values, setFieldValue } = useFormikContext<FormValues>();

  const [searchParams] = useSearchParams();

  const destinationCountryCodeSearchParam =
    searchParams.get('dest_country_code');

  useEffect(() => {
    if (
      !destinationCountryCodeSearchParam ||
      !values.departure_country ||
      values.destination_country
    )
      return;

    const foundCountry: CountryDetails | undefined = _.cloneDeep(
      store.shipmentCountriesStore.receiverCountries.find(
        (x) => x.code === destinationCountryCodeSearchParam.toLocaleUpperCase()
      )
    );

    if (!foundCountry) {
      setFieldValue('destination_country', null);
      return;
    }

    if (isZipSearch) {
      const foundCountryCapital = store.shipmentCountriesStore.getCapital(
        foundCountry.code
      );
      foundCountry.zipCode = foundCountryCapital?.default_zip;
      foundCountry.city = foundCountryCapital?.default_city;

      setFieldValue('destination_country', foundCountry);

      setFieldValue(
        'destination_search_string',
        `${foundCountry.zipCode}, ${foundCountry.city}`
      );
    } else {
      setDestinationCountryFieldDisabled?.(true);
      setFieldValue('destination_search_string', foundCountry.localized_name);
    }

    showInput('destination_search_string', true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    destinationCountryCodeSearchParam,
    values.departure_country,
    values.destination_country,
  ]);

  return null;
};
